import _ from "lodash";
/**--external--*/
import React, { useState, useRef, cloneElement } from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import classNames from "classnames";
import { Dialog } from "@toddle-edu/ds-web";

/**--internal--*/
import { IbAandE3D, Journal3D } from "@toddle-edu/ds-icons";
import InputTextField from "UIComponents/InputTextField";
import I18nHOC from "UIComponents/I18nHOC";
import { editCycle, deleteCycle } from "SnP/modules/SnPModule";
import { onEnterOrSpaceKeyDown } from "Utils";
import { AUTHORIZATION, EVALUATION } from "SnP/modules/SnPUtils";

/**--relative--*/
import { CYCLE_TYPE_LOCALE_MAPPING } from "../SnPCyclesFeedUtils";
import styles from "./CycleCardStyles";
import classes from "./CycleCard.scss";

const CycleCard = props => {
  const {
    cycle,
    feedType,
    onClickCard,
    editCycle,
    deleteCycle,
    isChecked,
    cycleActionsElement,
    t,
    testId,
    titleSuffix,
    cardStyles = {},
    isSchoolNameVisible = false,
  } = props;

  const { id, title: initialTitle, cycleType, curriculumProgram } = cycle;

  const [showEditableTitle, toggleEditableTitle] = useState(false);
  const [title, changeTitle] = useState(initialTitle);
  const [showDeleteConfirmation, toggleDeleteConfirmation] = useState(false);

  const titleRef = useRef();

  const onClickCardLocal = () => {
    if (!showEditableTitle) onClickCard?.({ id });
  };

  const handleKeyDownCardLocal = e => {
    if (!e.defaultPrevented && !showEditableTitle)
      onEnterOrSpaceKeyDown(e, onClickCardLocal);
  };

  const closeDeleteModal = () => toggleDeleteConfirmation(false);

  const curriculumProgramLabel = _.get(curriculumProgram, "label", "");

  const onClickDeleteCycle = async () => {
    await deleteCycle({
      id,
    });
  };

  const editCycleDetails = async ({ key, value }) => {
    try {
      await editCycle({
        cycleId: id,
        key,
        value,
      });
    } catch {
      changeTitle(initialTitle);
    }
  };

  const editCycleTitle = () => {
    toggleEditableTitle(false);

    /** reset if there are any errors or if title is the same */
    if (titleRef.current.isValid() || title === initialTitle) {
      changeTitle(initialTitle);
      return;
    }

    editCycleDetails({
      key: "title",
      value: title,
    });
  };

  const onDropDownItemClick = actionType => {
    switch (actionType) {
      case "ARCHIVE":
        editCycleDetails({
          key: "isArchived",
          value: true,
        });
        break;
      case "UNARCHIVE":
        editCycleDetails({
          key: "isArchived",
          value: false,
        });
        break;
      case "DELETE":
        toggleDeleteConfirmation(true);
        break;
      default:
        toggleEditableTitle(true);
        break;
    }
  };

  const handleKeyPress = e => {
    if (e.key === "Enter") editCycleTitle();
  };

  const componentTestId = testId ? `${testId}-cycleCard-${id}` : undefined;

  const getCycleActionsConfig = () => {
    switch (feedType) {
      case "EDIT":
        return {
          cycle,
          onDropDownItemClick,
          testId: componentTestId,
        };
      case "SELECT":
        return {
          isChecked,
        };
      default:
        return {};
    }
  };

  const typeLabel = t(CYCLE_TYPE_LOCALE_MAPPING[cycleType ?? EVALUATION]);

  const titleClasses = classNames("text-label", classes.title);
  const subTextClasses = classNames("text-body-s", classes.standards);
  const containerClasses = classNames(classes.container, {
    [classes.containerWithClick]: !!onClickCard,
  });

  return (
    <>
      <li
        className={containerClasses}
        style={cardStyles.cardContainerStyle}
        data-test-id={`${testId}-cycleCard-${id}`}
        onClick={e => {
          if (!e.defaultPrevented) {
            onClickCardLocal();
          }
        }}
        onKeyDown={handleKeyDownCardLocal}
        tabIndex="0"
      >
        <div className={classes.cycleDetails}>
          <div
            className={classNames(classes.cardIcon, {
              [classes.authorizationIcon]: cycleType === AUTHORIZATION,
              [classes.evaluationIcon]: cycleType === EVALUATION,
            })}
          >
            {cycleType == AUTHORIZATION ? (
              <IbAandE3D height={80} width={80} />
            ) : (
              <Journal3D height={80} width={80} />
            )}
          </div>
          <div className={classes.labels}>
            {showEditableTitle ? (
              <InputTextField
                type={"text"}
                autoFocus={true}
                value={title}
                inputStyle={styles.titleInputStyle}
                updateInputField={value => changeTitle(value)}
                handleKeyPress={handleKeyPress}
                onBlurInputField={editCycleTitle}
                isRequired={true}
                testId={`${testId}-cycleCard-${id}`}
                ref={titleRef}
              />
            ) : (
              <div className={classes.titleContainer}>
                <div
                  dir="auto"
                  style={cardStyles.titleStyle}
                  className={titleClasses}
                >
                  {title}
                </div>
                {titleSuffix ? titleSuffix : null}
              </div>
            )}

            <div style={cardStyles.standardsStyle} className={subTextClasses}>
              {isSchoolNameVisible ? (
                <>
                  <span dir="auto">{curriculumProgramLabel} </span>
                  <span
                    style={cardStyles.separatorStyle}
                    className={classes.separator}
                  >
                    •{" "}
                  </span>
                </>
              ) : null}
              {typeLabel}{" "}
              <span
                style={cardStyles.separatorStyle}
                className={classes.separator}
              >
                •{" "}
              </span>
              <span dir="auto">{_.get(cycle, "set.title", "")}</span>
            </div>
          </div>
        </div>
        <div className={classes.cycleActions}>
          {cycleActionsElement &&
            cloneElement(cycleActionsElement, getCycleActionsConfig())}
        </div>
      </li>
      {showDeleteConfirmation && (
        <Dialog
          testId={`${testId}-cycleCard-${id}`}
          dsVersion="2.0"
          onClose={closeDeleteModal}
          title={t("common:delete_with_label", { label: t("snp:cycle") })}
          message={t("common:delete_msg_with_label", {
            label: t("snp:cycle"),
          })}
          buttonOneText={t("common:cancel")}
          buttonTwoText={t("common:delete")}
          onButtonOneClick={closeDeleteModal}
          onButtonTwoClick={onClickDeleteCycle}
        />
      )}
    </>
  );
};

const mapActionCreators = {
  editCycle,
  deleteCycle,
};

export default compose(
  I18nHOC({ resource: ["common", "snp"] }),
  connect(null, mapActionCreators)
)(CycleCard);

CycleCard.defaultProps = {
  testId: "appComponents-SnPCyclesFeedV2",
};
